import { getStrapiToken } from '@/services/auth/getStrapiToken';
// @/services/app/store/modules/ca-spend-distribution.js

/**
 * SpendDistributionHandler
 * 
 * Manages spend distribution data for carbon accounting:
 * - API integration for retrieving and manipulating distribution data
 * - Data transformation between API and UI formats
 * - CRUD operations with validation and error handling
 * - Real-time updates via WebSocket connection
 * - Integration with emissions recalculation workflow
 */
export class SpendDistributionHandler {
  /**
   * Initialize the spend distribution handler with a store reference
   * @param {Object} store - The main application store instance
   */
  constructor(store) {
    this.store = store;

    // Register with WebSocketHandler if available
    if (this.store.webSocketHandler) {
      this.store.webSocketHandler.registerHandler(
        'SpendDistributionHandler',
        this.handleUpdate.bind(this),
        [
          'spend-distribution',
          'spend-distribution:update',
          'spend-distribution:create',
          'spend-distribution:delete'
        ],
        this
      );
    }
  }

  /**
   * Load all spend distribution data from API
   * @returns {Promise<Object>} Spend distribution data grouped by year
   * @throws {Error} If API request fails
   */
  async loadSpendDistribution() {
    try {
      this.store.state.loading.spendDistribution = true;
      this.store.notifySubscribers('loading');
      const projectId = this.store.getProjectId();

      // Early return if no project ID is available
      if (!projectId) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Cannot load spend distribution: No project ID available');
        }
        return {};
      }

      // Use new project route
      const url = `${process.env.GATSBY_STRAPI_URL}/api/ca-spend-distributions/project/${projectId}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${getStrapiToken()}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch spend distribution data: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const { data: rawData } = await response.json();

      // Transform and group by year
      const byYear = this.processRawDistributionData(rawData);

      this.store.state.data.spendDistribution = {
        byYear,
        meta: {
          lastSync: new Date().toISOString()
        }
      };

      this.store.notifySubscribers('spendDistribution');
      return byYear;

    } catch (error) {
      console.error('Spend distribution load failed:', error);
      this.store.handleError('spendDistribution', error);
      return {};
    } finally {
      this.store.state.loading.spendDistribution = false;
      this.store.notifySubscribers('loading');
    }
  }

  /**
   * Process raw distribution data into year-based structure
   * @private
   * @param {Array} rawData - Raw data from API
   * @returns {Object} Processed data grouped by year
   */
  processRawDistributionData(rawData) {
    if (!Array.isArray(rawData)) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Invalid spend distribution data received:', rawData);
      }
      return {};
    }

    const byYear = {};

    // Process all distributions
    rawData.forEach(item => {
      const data = item.attributes || item;
      const year = data.Year?.toString();

      if (!year) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Skipping distribution without year:', item);
        }
        return;
      }

      if (!byYear[year]) {
        byYear[year] = [];
      }

      // Add transformed data to year group
      byYear[year].push({
        id: item.id,
        distributionId: data.DistributionId,
        scope3SectorId: data.Scope3SectorId,
        shareOfInput: parseFloatSafe(data.ShareOfInput),
        snowflakeId: data.SnowflakeId || '',
        resourceId: data.ResourceId || '',
        year,
        createdAt: data.createdAt || new Date().toISOString(),
        updatedAt: data.updatedAt || new Date().toISOString(),
        lastUpdatedOn: data.SnowflakeLastUpdatedOn || data.updatedAt || new Date().toISOString()
      });
    });

    return byYear;
  }

  /**
   * Create a new spend distribution
   * @param {Object} data - Spend distribution data
   * @param {string|number} data.Year - Year for the distribution (or lowercase 'year')
   * @param {string} data.scope3SectorId - Sector ID for the distribution
   * @param {string|number} data.shareOfInput - Share of input as percentage
   * @param {string} [data.distributionId] - Optional distribution ID (generated if not provided)
   * @returns {Promise<boolean>} Success status
   * @throws {Error} If validation fails or API request fails
   */
  async createSpendDistribution(data) {
    try {
      const projectId = this.store.getProjectId();

      // Validate inputs
      if (!projectId) {
        throw new Error('Project ID is required to create spend distribution');
      }

      // Check for lowercase or uppercase year property - prioritize uppercase for API consistency
      const yearValue = data.Year || data.year;
      if (!yearValue) {
        throw new Error('Year is required');
      }

      if (!data.scope3SectorId) {
        throw new Error('Scope3SectorId is required');
      }

      // Validate shareOfInput is a non-negative number
      const shareOfInput = parseFloatSafe(data.shareOfInput);
      if (shareOfInput < 0 || shareOfInput > 100) {
        throw new Error('Share of input must be between 0 and 100 percent');
      }

      // 1. Create in Strapi
      const response = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/ca-spend-distributions/project/${projectId}/create`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getStrapiToken()}`
          },
          body: JSON.stringify({
            data: {
              Year: yearValue.toString(),
              Scope3SectorId: data.scope3SectorId.toString(),
              ShareOfInput: shareOfInput.toString(),
              DistributionId: data.distributionId || crypto.randomUUID()
            }
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `Failed to create spend distribution: ${response.statusText}`);
      }

      const result = await response.json();

      // 2. Sync to Snowflake
      const syncResponse = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/ca-spend-distributions/project/${projectId}/${result.data.id}/sync`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${getStrapiToken()}`
          }
        }
      );

      if (!syncResponse.ok) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Distribution created but sync to Snowflake failed. Will retry on next calculation.');
        }
      }

      // 3. Flag for recalculation using shared utility
      await this.store.emissionsHandler.flagYearForRecalculation(yearValue);

      // 4. Reload data
      await this.loadSpendDistribution();

      // 5. Save state
      await this.store.saveState();

      return true;

    } catch (error) {
      console.error('Failed to create spend distribution:', error);
      this.store.handleError('spendDistribution', error);
      throw error;
    }
  }

  /**
   * Update an existing spend distribution
   * @param {string} documentId - Document ID to update
   * @param {Object} updates - Updates to apply
   * @param {string|number} [updates.shareOfInput] - New share of input value
   * @returns {Promise<boolean>} Success status
   * @throws {Error} If validation fails or API request fails
   */
  async updateSpendDistribution(documentId, updates) {
    try {
      const projectId = this.store.getProjectId();

      // Validate inputs
      if (!projectId) {
        throw new Error('Project ID is required to update spend distribution');
      }

      if (!documentId) {
        throw new Error('Document ID is required');
      }

      // If shareOfInput is provided, validate it's a valid number
      if (updates.shareOfInput !== undefined) {
        const shareOfInput = parseFloatSafe(updates.shareOfInput);
        if (shareOfInput < 0 || shareOfInput > 100) {
          throw new Error('Share of input must be between 0 and 100 percent');
        }

        // Ensure it's a string for consistency
        updates.shareOfInput = shareOfInput.toString();
      }

      // Get year info for flagging recalculation
      let year = null;
      try {
        // Try to find the distribution in the current state to get its year
        const allYears = this.store.state.data.spendDistribution.byYear || {};
        for (const [yearKey, distributions] of Object.entries(allYears)) {
          const found = distributions.find(d => d.id === documentId);
          if (found) {
            year = yearKey;
            break;
          }
        }
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Error finding year for distribution:', err);
        }
      }

      // 1. Update in Strapi
      const response = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/ca-spend-distributions/project/${projectId}/${documentId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getStrapiToken()}`
          },
          body: JSON.stringify({
            data: {
              ShareOfInput: updates.shareOfInput
            }
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `Failed to update spend distribution: ${response.statusText}`);
      }

      // 2. Sync to Snowflake 
      const syncResponse = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/ca-spend-distributions/project/${projectId}/${documentId}/sync`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${getStrapiToken()}`
          }
        }
      );

      if (!syncResponse.ok) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Distribution updated but sync to Snowflake failed. Will retry on next calculation.');
        }
      }

      // 3. Flag for recalculation using shared utility if we have the year
      if (year) {
        await this.store.emissionsHandler.flagYearForRecalculation(year);
      }

      // 4. Reload data
      await this.loadSpendDistribution();

      // 5. Save state
      await this.store.saveState();

      return true;

    } catch (error) {
      console.error('Failed to update spend distribution:', error);
      this.store.handleError('spendDistribution', error);
      throw error;
    }
  }

  /**
   * Delete a spend distribution
   * @param {string} documentId - Document ID to delete
   * @returns {Promise<boolean>} Success status
   * @throws {Error} If validation fails or API request fails
   */
  async deleteSpendDistribution(documentId) {
    try {
      const projectId = this.store.getProjectId();

      // Validate inputs
      if (!projectId) {
        throw new Error('Project ID is required to delete spend distribution');
      }

      if (!documentId) {
        throw new Error('Document ID is required');
      }

      // Get year info for flagging recalculation
      let year = null;
      try {
        // Try to find the distribution in the current state to get its year
        const allYears = this.store.state.data.spendDistribution.byYear || {};
        for (const [yearKey, distributions] of Object.entries(allYears)) {
          const found = distributions.find(d => d.id === documentId);
          if (found) {
            year = yearKey;
            break;
          }
        }
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Error finding year for deleted distribution:', err);
        }
      }

      // 1. Delete from Strapi (backend handles Snowflake deletion directly)
      const deleteUrl = `${process.env.GATSBY_STRAPI_URL}/api/ca-spend-distributions/project/${projectId}/${documentId}`;
      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${getStrapiToken()}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `Failed to delete spend distribution: ${response.statusText}`);
      }

      // REMOVED: The problematic cleanup sync that was deleting ALL distributions
      // The backend controller already handles the Snowflake deletion directly

      // 2. Flag year for recalculation using shared utility if we have the year
      if (year) {
        await this.store.emissionsHandler.flagYearForRecalculation(year);
      }

      // 3. Reload data
      await this.loadSpendDistribution();

      // Clean up orphaned supplier inputs
      await this.store.supplierInputHandler.cleanupOrphanedSupplierInputs();

      // 4. Save state
      await this.store.saveState();

      return true;

    } catch (error) {
      console.error('Failed to delete spend distribution:', error);
      this.store.handleError('spendDistribution', error);
      throw error;
    }
  }

  /**
   * Handle real-time spend distribution updates from WebSocket
   * @param {Object} data - Update data from WebSocket
   * @returns {Promise<void>}
   */
  async handleUpdate(data) {
    try {
      // Simply reload the data
      await this.loadSpendDistribution();

      // Extract year for notification message
      const year = data.year || data.Year;

      // Flag for recalculation if we have the year
      if (year) {
        await this.store.emissionsHandler.flagYearForRecalculation(year);
      }

      // Notification message
      const message = year
        ? `Spend distribution data for ${year} updated successfully`
        : 'Spend distribution data updated successfully';

      this.store.notifySubscribers('syncStatus', {
        type: 'success',
        message,
        timestamp: new Date(),
        details: {
          updatedAt: new Date().toISOString(),
          source: 'websocket'
        }
      });

    } catch (error) {
      console.error('Failed to reload spend distribution data:', error);
      this.store.handleError('spendDistribution', {
        message: error.message,
        details: {
          timestamp: new Date().toISOString(),
          source: 'websocket',
          originalError: error
        }
      });
    }
  }

  /**
   * Get spend distribution data for specific year
   * @param {string|number} year - Year to get data for
   * @returns {Array|null} Spend distribution data or null if not loaded
   */
  getSpendDistributionData(year) {
    return this.store.state.data.spendDistribution.byYear[year] || null;
  }

  /**
   * Check if spend distribution data exists for year
   * @param {string|number} year - Year to check
   * @returns {boolean} True if data exists
   */
  hasSpendDistributionData(year) {
    return !!this.store.state.data.spendDistribution.byYear[year];
  }

  /**
   * Get all available years with spend distribution data
   * @returns {Array<string>} Array of years sorted in descending order
   */
  getAvailableYears() {
    return Object.keys(this.store.state.data.spendDistribution.byYear)
      .sort((a, b) => b.localeCompare(a));
  }
}

/**
 * Safely parse float with fallback to 0
 * @param {string|number} value - Value to parse
 * @returns {number} Parsed float or 0 if invalid
 */
function parseFloatSafe(value) {
  if (value === null || value === undefined) return 0;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
}